import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, UserPlus, Search, Mail, Phone, Package, Download, Upload, Tag, X, Filter, Check, Edit2, Tags } from 'lucide-react';
import AppHeader from '../components/AppHeader';

// Predefined tag colors
const TAG_COLORS = {
  'VIP': '#ef4444',
  'Lead': '#f59e0b',
  'Customer': '#10b981',
  'Partner': '#3b82f6',
  'Vendor': '#8b5cf6',
  'Prospect': '#ec4899',
  'Inactive': '#6b7280',
};

const SUGGESTED_TAGS = ['VIP', 'Lead', 'Customer', 'Partner', 'Vendor', 'Prospect', 'Inactive'];

const getTagColor = (tag) => {
  return TAG_COLORS[tag] || '#6b7280';
};

const Contacts = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Selection state for bulk operations
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Inline editing state
  const [editingTagsFor, setEditingTagsFor] = useState(null);
  const [editingTags, setEditingTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  
  // Bulk tag assignment state
  const [bulkTagsToAdd, setBulkTagsToAdd] = useState([]);
  const [bulkTagsToRemove, setBulkTagsToRemove] = useState([]);
  const [bulkNewTag, setBulkNewTag] = useState('');
  
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    additional_emails_text: '', // comma-separated UI field; converted on submit
    phone: '',
    company: '',
    notes: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchCustomers();
    fetchTags();
  }, []);

  useEffect(() => {
    let filtered = customers;
    
    if (searchQuery) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (customer.company && customer.company.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    if (selectedTag) {
      filtered = filtered.filter(customer => 
        customer.tags && customer.tags.includes(selectedTag)
      );
    }
    
    setFilteredCustomers(filtered);
  }, [searchQuery, customers, selectedTag]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/customers');
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get('/customers/tags');
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.log('Could not fetch tags');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    
    try {
      // Convert the comma-separated UI field into a clean array before posting.
      const additional_emails = (newCustomer.additional_emails_text || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      // eslint-disable-next-line no-unused-vars
      const { additional_emails_text, ...rest } = newCustomer;
      await axios.post('/customers', { ...rest, additional_emails });
      toast.success('Contact created successfully');
      setDialogOpen(false);
      setNewCustomer({ name: '', email: '', additional_emails_text: '', phone: '', company: '', notes: '', tags: [] });
      fetchCustomers();
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get('/customers/export/csv', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Contacts exported successfully');
    } catch (error) {
      toast.error('Failed to export contacts');
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post('/customers/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} contacts`);
        fetchCustomers();
        fetchTags();
      }
      
      if (response.data.skipped > 0) {
        toast.warning(`Skipped ${response.data.skipped} rows`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Inline tag editing functions
  const startEditingTags = (customer, e) => {
    e.stopPropagation();
    setEditingTagsFor(customer.id);
    setEditingTags([...(customer.tags || [])]);
    setNewTagInput('');
  };

  const saveInlineTags = async (customerId) => {
    try {
      await axios.put(`/customers/${customerId}/tags`, editingTags);
      toast.success('Tags updated');
      setEditingTagsFor(null);
      fetchCustomers();
      fetchTags();
    } catch (error) {
      toast.error('Failed to update tags');
    }
  };

  const cancelEditingTags = () => {
    setEditingTagsFor(null);
    setEditingTags([]);
    setNewTagInput('');
  };

  const addInlineTag = () => {
    if (newTagInput && !editingTags.includes(newTagInput)) {
      setEditingTags([...editingTags, newTagInput]);
      setNewTagInput('');
    }
  };

  const removeInlineTag = (tagToRemove) => {
    setEditingTags(editingTags.filter(t => t !== tagToRemove));
  };

  const toggleSuggestedTag = (tag) => {
    if (editingTags.includes(tag)) {
      setEditingTags(editingTags.filter(t => t !== tag));
    } else {
      setEditingTags([...editingTags, tag]);
    }
  };

  // Bulk selection functions
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedContacts([]);
  };

  const toggleContactSelection = (customerId, e) => {
    e.stopPropagation();
    if (selectedContacts.includes(customerId)) {
      setSelectedContacts(selectedContacts.filter(id => id !== customerId));
    } else {
      setSelectedContacts([...selectedContacts, customerId]);
    }
  };

  const selectAllContacts = () => {
    if (selectedContacts.length === filteredCustomers.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(filteredCustomers.map(c => c.id));
    }
  };

  // Bulk tag functions
  const openBulkTagDialog = () => {
    setBulkTagsToAdd([]);
    setBulkTagsToRemove([]);
    setBulkNewTag('');
    setBulkTagDialogOpen(true);
  };

  const toggleBulkTagToAdd = (tag) => {
    if (bulkTagsToAdd.includes(tag)) {
      setBulkTagsToAdd(bulkTagsToAdd.filter(t => t !== tag));
    } else {
      setBulkTagsToAdd([...bulkTagsToAdd, tag]);
      setBulkTagsToRemove(bulkTagsToRemove.filter(t => t !== tag));
    }
  };

  const toggleBulkTagToRemove = (tag) => {
    if (bulkTagsToRemove.includes(tag)) {
      setBulkTagsToRemove(bulkTagsToRemove.filter(t => t !== tag));
    } else {
      setBulkTagsToRemove([...bulkTagsToRemove, tag]);
      setBulkTagsToAdd(bulkTagsToAdd.filter(t => t !== tag));
    }
  };

  const addBulkNewTag = () => {
    if (bulkNewTag && !bulkTagsToAdd.includes(bulkNewTag)) {
      setBulkTagsToAdd([...bulkTagsToAdd, bulkNewTag]);
      setBulkNewTag('');
    }
  };

  const applyBulkTags = async () => {
    if (selectedContacts.length === 0) {
      toast.error('No contacts selected');
      return;
    }

    try {
      let successCount = 0;
      
      for (const customerId of selectedContacts) {
        const customer = customers.find(c => c.id === customerId);
        if (!customer) continue;
        
        let newTags = [...(customer.tags || [])];
        
        // Add tags
        for (const tag of bulkTagsToAdd) {
          if (!newTags.includes(tag)) {
            newTags.push(tag);
          }
        }
        
        // Remove tags
        newTags = newTags.filter(t => !bulkTagsToRemove.includes(t));
        
        await axios.put(`/customers/${customerId}/tags`, newTags);
        successCount++;
      }
      
      toast.success(`Updated tags for ${successCount} contacts`);
      setBulkTagDialogOpen(false);
      setSelectedContacts([]);
      setSelectionMode(false);
      fetchCustomers();
      fetchTags();
    } catch (error) {
      toast.error('Failed to update some contacts');
    }
  };

  const addTagToNewCustomer = () => {
    if (newTag && !newCustomer.tags.includes(newTag)) {
      setNewCustomer({ ...newCustomer, tags: [...newCustomer.tags, newTag] });
      setNewTag('');
    }
  };

  const removeTagFromNewCustomer = (tagToRemove) => {
    setNewCustomer({ 
      ...newCustomer, 
      tags: newCustomer.tags.filter(t => t !== tagToRemove) 
    });
  };

  const viewCustomerDetails = (customerId) => {
    if (selectionMode) return;
    navigate(`/contacts/${customerId}`);
  };

  return (
    <div className="dashboard" data-testid="contacts-page">
      <AppHeader />

      <main className="dashboard-content">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }} data-testid="page-title">
            Contacts
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button 
              variant={selectionMode ? "default" : "outline"} 
              onClick={toggleSelectionMode}
              data-testid="bulk-select-btn"
            >
              <Check size={18} /> {selectionMode ? 'Cancel Selection' : 'Bulk Select'}
            </Button>
            <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
              <Download size={18} /> Export CSV
            </Button>
            <Button variant="outline" onClick={() => setImportDialogOpen(true)} data-testid="import-csv-btn">
              <Upload size={18} /> Import CSV
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar - Shows when contacts are selected */}
        {selectionMode && selectedContacts.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '12px 20px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'white'
          }} data-testid="bulk-actions-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Checkbox 
                checked={selectedContacts.length === filteredCustomers.length}
                onCheckedChange={selectAllContacts}
              />
              <span style={{ fontWeight: '600' }}>
                {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <Button 
              onClick={openBulkTagDialog}
              style={{ background: 'white', color: '#667eea' }}
              data-testid="bulk-tag-btn"
            >
              <Tags size={18} /> Assign Tags
            </Button>
          </div>
        )}

        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {/* Search and Actions Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <div style={{ position: 'relative' }}>
                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: '40px' }}
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-contact-button">
                  <UserPlus size={18} /> Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-contact-dialog" style={{ maxWidth: '500px' }}>
                <DialogHeader>
                  <DialogTitle>Add New Contact</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCustomer}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div className="form-group">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={newCustomer.name}
                        onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                        required
                        data-testid="new-contact-name"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newCustomer.email}
                        onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                        required
                        data-testid="new-contact-email"
                      />
                    </div>
                    <div className="form-group">
                      <Label htmlFor="additional_emails">Additional emails (CC)</Label>
                      <Input
                        id="additional_emails"
                        type="text"
                        placeholder="alias@org.com, team@org.com"
                        value={newCustomer.additional_emails_text}
                        onChange={(e) => setNewCustomer({ ...newCustomer, additional_emails_text: e.target.value })}
                        data-testid="new-contact-additional-emails"
                      />
                      <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                        Comma-separated. These addresses are automatically CC'd on every proposal &amp; payment email.
                      </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                          data-testid="new-contact-phone"
                        />
                      </div>
                      <div className="form-group">
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          value={newCustomer.company}
                          onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                          data-testid="new-contact-company"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="notes">Notes</Label>
                      <Input
                        id="notes"
                        value={newCustomer.notes}
                        onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                        data-testid="new-contact-notes"
                      />
                    </div>
                    
                    {/* Tags Section */}
                    <div className="form-group">
                      <Label>Tags</Label>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <Input
                          placeholder="Add a tag..."
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTagToNewCustomer())}
                          style={{ flex: 1 }}
                          list="tag-suggestions"
                        />
                        <datalist id="tag-suggestions">
                          {SUGGESTED_TAGS.map(tag => (
                            <option key={tag} value={tag} />
                          ))}
                        </datalist>
                        <Button type="button" variant="outline" onClick={addTagToNewCustomer}>
                          <Plus size={16} />
                        </Button>
                      </div>
                      {newCustomer.tags.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {newCustomer.tags.map(tag => (
                            <span
                              key={tag}
                              style={{
                                background: getTagColor(tag),
                                color: 'white',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              {tag}
                              <X 
                                size={14} 
                                style={{ cursor: 'pointer' }} 
                                onClick={() => removeTagFromNewCustomer(tag)}
                              />
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <Button type="submit" className="w-full" data-testid="submit-contact-button">
                      Create Contact
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tag Filter Row */}
          {allTags.length > 0 && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e5e7eb',
              flexWrap: 'wrap'
            }}>
              <Filter size={16} style={{ color: '#6b7280' }} />
              <span style={{ fontSize: '14px', color: '#6b7280', marginRight: '8px' }}>Filter by tag:</span>
              <button
                onClick={() => setSelectedTag(null)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '16px',
                  border: 'none',
                  background: selectedTag === null ? '#111827' : '#f3f4f6',
                  color: selectedTag === null ? 'white' : '#374151',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                All
              </button>
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '16px',
                    border: 'none',
                    background: selectedTag === tag ? getTagColor(tag) : '#f3f4f6',
                    color: selectedTag === tag ? 'white' : '#374151',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                >
                  <Tag size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  {tag}
                </button>
              ))}
            </div>
          )}

          {/* Contacts Grid */}
          {loading ? (
            <p>Loading contacts...</p>
          ) : filteredCustomers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#718096' }}>
              <UserPlus size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
              <p>{searchQuery || selectedTag ? 'No contacts found matching your criteria' : 'No contacts yet. Add your first contact!'}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.id}
                  onClick={() => viewCustomerDetails(customer.id)}
                  style={{
                    background: selectedContacts.includes(customer.id) ? '#ede9fe' : '#f7fafc',
                    padding: '20px',
                    borderRadius: '12px',
                    border: `2px solid ${selectedContacts.includes(customer.id) ? '#8b5cf6' : '#e2e8f0'}`,
                    cursor: selectionMode ? 'pointer' : 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectionMode) {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectionMode && !selectedContacts.includes(customer.id)) {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }
                  }}
                  data-testid={`contact-card-${customer.id}`}
                >
                  {/* Selection Checkbox */}
                  {selectionMode && (
                    <div 
                      style={{ position: 'absolute', top: '12px', right: '12px' }}
                      onClick={(e) => toggleContactSelection(customer.id, e)}
                    >
                      <Checkbox 
                        checked={selectedContacts.includes(customer.id)}
                        data-testid={`select-contact-${customer.id}`}
                      />
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px',
                      fontWeight: '600'
                    }}>
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>
                        {customer.name}
                      </h3>
                      {customer.company && (
                        <p style={{ fontSize: '12px', color: '#718096' }}>{customer.company}</p>
                      )}
                    </div>
                  </div>

                  {/* Tags Section - Inline Editing */}
                  <div style={{ marginBottom: '12px', minHeight: '32px' }}>
                    {editingTagsFor === customer.id ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        {/* Suggested Tags */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                          {SUGGESTED_TAGS.map(tag => (
                            <button
                              key={tag}
                              onClick={() => toggleSuggestedTag(tag)}
                              style={{
                                padding: '2px 8px',
                                borderRadius: '10px',
                                border: 'none',
                                background: editingTags.includes(tag) ? getTagColor(tag) : '#e5e7eb',
                                color: editingTags.includes(tag) ? 'white' : '#374151',
                                fontSize: '11px',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                        {/* Custom Tag Input */}
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                          <Input
                            placeholder="Custom tag..."
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInlineTag())}
                            style={{ height: '28px', fontSize: '12px' }}
                          />
                          <Button size="sm" variant="outline" onClick={addInlineTag} style={{ height: '28px', padding: '0 8px' }}>
                            <Plus size={14} />
                          </Button>
                        </div>
                        {/* Current Tags */}
                        {editingTags.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
                            {editingTags.map(tag => (
                              <span
                                key={tag}
                                style={{
                                  background: getTagColor(tag),
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {tag}
                                <X size={12} style={{ cursor: 'pointer' }} onClick={() => removeInlineTag(tag)} />
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Save/Cancel Buttons */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <Button size="sm" onClick={() => saveInlineTags(customer.id)} style={{ height: '26px', fontSize: '11px' }}>
                            <Check size={12} /> Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEditingTags} style={{ height: '26px', fontSize: '11px' }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                        {customer.tags && customer.tags.length > 0 ? (
                          customer.tags.map(tag => (
                            <span
                              key={tag}
                              style={{
                                background: getTagColor(tag),
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>No tags</span>
                        )}
                        {!selectionMode && (
                          <button
                            onClick={(e) => startEditingTags(customer, e)}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '6px',
                              border: '1px dashed #d1d5db',
                              background: 'transparent',
                              color: '#6b7280',
                              fontSize: '11px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '2px'
                            }}
                            data-testid={`edit-tags-${customer.id}`}
                          >
                            <Edit2 size={10} /> Edit
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#4a5568' }}>
                      <Mail size={14} />
                      <span>{customer.email}</span>
                    </div>
                    {customer.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#4a5568' }}>
                        <Phone size={14} />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#667eea', marginTop: '8px' }}>
                      <Package size={14} />
                      <span style={{ fontWeight: '600' }}>{customer.proposal_count || 0} Order(s)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle>Import Contacts from CSV</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ 
              background: '#f0f9ff', 
              padding: '16px', 
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#0369a1' }}>
                CSV Format Requirements
              </h4>
              <p style={{ fontSize: '13px', color: '#0c4a6e', marginBottom: '8px' }}>
                Your CSV should include these columns (headers):
              </p>
              <ul style={{ fontSize: '12px', color: '#0c4a6e', paddingLeft: '20px', margin: 0 }}>
                <li><strong>Name</strong> (required)</li>
                <li><strong>Email</strong> (required)</li>
                <li>Phone</li>
                <li>Company</li>
                <li>Tags (comma-separated)</li>
                <li>Notes</li>
              </ul>
            </div>
            
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                style={{ display: 'none' }}
                id="csv-upload"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="w-full"
                style={{ height: '48px' }}
              >
                {importing ? 'Importing...' : (
                  <>
                    <Upload size={18} /> Select CSV File
                  </>
                )}
              </Button>
            </div>
            
            {importResult && (
              <div style={{
                background: importResult.imported > 0 ? '#f0fdf4' : '#fef2f2',
                padding: '16px',
                borderRadius: '8px',
                border: `1px solid ${importResult.imported > 0 ? '#86efac' : '#fecaca'}`
              }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Import Results
                </h4>
                <p style={{ fontSize: '13px', marginBottom: '4px' }}>
                  ✅ Imported: <strong>{importResult.imported}</strong> contacts
                </p>
                <p style={{ fontSize: '13px', marginBottom: '8px' }}>
                  ⚠️ Skipped: <strong>{importResult.skipped}</strong> rows
                </p>
                {importResult.errors.length > 0 && (
                  <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    <p style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Errors:</p>
                    {importResult.errors.map((err, idx) => (
                      <p key={idx} style={{ fontSize: '11px', color: '#dc2626', marginBottom: '2px' }}>
                        {err}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <Button 
              variant="outline" 
              onClick={() => {
                setImportDialogOpen(false);
                setImportResult(null);
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Assignment Dialog */}
      <Dialog open={bulkTagDialogOpen} onOpenChange={setBulkTagDialogOpen}>
        <DialogContent style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle>Bulk Tag Assignment</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ fontSize: '14px', color: '#6b7280' }}>
              Assign or remove tags for <strong>{selectedContacts.length}</strong> selected contact{selectedContacts.length !== 1 ? 's' : ''}
            </p>
            
            {/* Tags to Add */}
            <div>
              <Label style={{ marginBottom: '8px', display: 'block' }}>Tags to Add (click to select)</Label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {SUGGESTED_TAGS.map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleBulkTagToAdd(tag)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '16px',
                      border: bulkTagsToAdd.includes(tag) ? 'none' : '1px solid #d1d5db',
                      background: bulkTagsToAdd.includes(tag) ? getTagColor(tag) : 'white',
                      color: bulkTagsToAdd.includes(tag) ? 'white' : '#374151',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {bulkTagsToAdd.includes(tag) && <Check size={12} style={{ display: 'inline', marginRight: '4px' }} />}
                    {tag}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Input
                  placeholder="Custom tag to add..."
                  value={bulkNewTag}
                  onChange={(e) => setBulkNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBulkNewTag())}
                  style={{ flex: 1 }}
                />
                <Button variant="outline" onClick={addBulkNewTag}>
                  <Plus size={16} />
                </Button>
              </div>
              {bulkTagsToAdd.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#10b981' }}>+ Will add: </span>
                  {bulkTagsToAdd.map(tag => (
                    <span key={tag} style={{ 
                      background: '#d1fae5', 
                      color: '#065f46', 
                      padding: '2px 8px', 
                      borderRadius: '8px', 
                      fontSize: '11px',
                      marginRight: '4px'
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Tags to Remove */}
            {allTags.length > 0 && (
              <div>
                <Label style={{ marginBottom: '8px', display: 'block' }}>Tags to Remove (click to select)</Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleBulkTagToRemove(tag)}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '16px',
                        border: bulkTagsToRemove.includes(tag) ? 'none' : '1px solid #d1d5db',
                        background: bulkTagsToRemove.includes(tag) ? '#ef4444' : 'white',
                        color: bulkTagsToRemove.includes(tag) ? 'white' : '#374151',
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        textDecoration: bulkTagsToRemove.includes(tag) ? 'line-through' : 'none'
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                {bulkTagsToRemove.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#ef4444' }}>- Will remove: </span>
                    {bulkTagsToRemove.map(tag => (
                      <span key={tag} style={{ 
                        background: '#fee2e2', 
                        color: '#991b1b', 
                        padding: '2px 8px', 
                        borderRadius: '8px', 
                        fontSize: '11px',
                        marginRight: '4px',
                        textDecoration: 'line-through'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              <Button 
                onClick={applyBulkTags}
                disabled={bulkTagsToAdd.length === 0 && bulkTagsToRemove.length === 0}
                className="flex-1"
              >
                Apply to {selectedContacts.length} Contact{selectedContacts.length !== 1 ? 's' : ''}
              </Button>
              <Button variant="outline" onClick={() => setBulkTagDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
